import { Form } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import {
  type ChangeEvent,
  type FocusEvent,
  type ReactNode,
  type RefObject,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from "@/components/responsive-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useOnline } from "@/hooks/use-online"
import { adminInvitationsPath } from "@/routes"

export type TenantKind = "brand" | "agency"

interface NewTenantDialogProps {
  /**
   * `"brand"` or `"agency"` — drives:
   *   - dialog title + field labels (`admin.invites.dialog.new_brand.*`
   *     vs `…new_agency.*`)
   *   - the `path` body field POSTed to `Admin::InvitationsController` so
   *     the single-controller dispatch routes to the right
   *     `Admin::Tenant::Create*WithFirstAdminInvite` service per Backend
   *     TRD.
   *   - testid namespace: `admin-new-{brand,agency}-{trigger,dialog,submit}`.
   */
  kind: TenantKind
  /** Trigger element passed via `<DialogTrigger asChild>`. */
  trigger: ReactNode
  /**
   * Optional ref forwarded for explicit focus return on close. Standard
   * shadcn flow already covers the trigger-on-page case; pass this when
   * mounting via state-controlled `open` from a callback prop (e.g.
   * `PendingFirstAdminInviteCard`'s orphan EmptyState).
   */
  triggerRef?: RefObject<HTMLElement | null>
}

/**
 * OKS-12 Paths B + C — Create Brand / Create Agency + first admin invite.
 *
 * Same shape, two surfaces. Backend's single endpoint
 * (`POST /admin/invitations`) dispatches on the hidden `path` field so
 * one controller serves all three paths (A handled by `InviteAdminDialog`,
 * B + C by this component keyed on `kind`).
 *
 * Three fields per UX Spec / PRD §6:
 *   - `name` (required) — tenant display name; backend derives slug.
 *   - `locale` (FR default, EN) — drives the invitation email locale
 *     (PRD Rule 2 / AC2).
 *   - `admin_email` (required) — invitee for the first admin-tier
 *     account (BE6).
 *
 * Atomic per PRD AC2: backend wraps tenant create + invitation create +
 * mailer-deliver-later in a single `requires_new: true` transaction —
 * partial state cannot land. If validation fails, the FE re-renders with
 * field-level errors via the Inertia render-prop. If a transactional
 * failure surfaces, it lands in `errors.base` and renders in the top
 * banner.
 *
 * Crew form rules: Inertia `<Form>` (NOT shadcn `Form`); plain `Input` +
 * `Label` + `<InputError>` + Radix `<Select>` with `name`-via-hidden-input
 * for form submission; no react-hook-form; no zod.
 *
 * Locked QA testid:
 *   - `admin-new-{brand,agency}-trigger` on the trigger element
 *   - `admin-new-{brand,agency}-dialog` on the DialogContent
 *   - `admin-new-{brand,agency}-submit` on the primary button
 */
export function NewTenantDialog({
  kind,
  trigger,
  triggerRef,
}: NewTenantDialogProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  const [locale, setLocale] = useState("fr")
  const [email, setEmail] = useState("")
  /**
   * Mid-submit guard for `onOpenChange` — same pattern as
   * `InviteAdminDialog`. Mirrors `processing` from the render-prop into
   * a ref every render so ESC + overlay-click can read the latest value
   * outside the Form scope.
   */
  const submittingRef = useRef(false)

  const isBrand = kind === "brand"
  const testId = isBrand ? "admin-new-brand" : "admin-new-agency"

  /**
   * Resolve every i18n key as a literal-argument `t(...)` call. The
   * inertia_i18n health scanner only matches string-literal arguments —
   * dynamic template strings (`labels.title`)
   * silently bypass the parity check. Pre-resolving here keeps the
   * scanner happy without duplicating the JSX tree per `kind`.
   */
  const labels = isBrand
    ? {
        title: t("admin.invites.dialog.new_brand.title"),
        description: t("admin.invites.dialog.new_brand.description"),
        name_label: t("admin.invites.dialog.new_brand.name_label"),
        name_placeholder: t("admin.invites.dialog.new_brand.name_placeholder"),
        locale_label: t("admin.invites.dialog.new_brand.locale_label"),
        email_label: t("admin.invites.dialog.new_brand.email_label"),
        email_placeholder: t(
          "admin.invites.dialog.new_brand.email_placeholder",
        ),
        submit: t("admin.invites.dialog.new_brand.submit"),
        creating: t("admin.invites.dialog.new_brand.creating"),
        cancel: t("admin.invites.dialog.new_brand.cancel"),
      }
    : {
        title: t("admin.invites.dialog.new_agency.title"),
        description: t("admin.invites.dialog.new_agency.description"),
        name_label: t("admin.invites.dialog.new_agency.name_label"),
        name_placeholder: t("admin.invites.dialog.new_agency.name_placeholder"),
        locale_label: t("admin.invites.dialog.new_agency.locale_label"),
        email_label: t("admin.invites.dialog.new_agency.email_label"),
        email_placeholder: t(
          "admin.invites.dialog.new_agency.email_placeholder",
        ),
        submit: t("admin.invites.dialog.new_agency.submit"),
        creating: t("admin.invites.dialog.new_agency.creating"),
        cancel: t("admin.invites.dialog.new_agency.cancel"),
      }

  function handleEmailBlur(event: FocusEvent<HTMLInputElement>) {
    const next = event.target.value.trim().toLowerCase()
    if (next !== email) setEmail(next)
  }

  function handleClose() {
    setOpen(false)
    setName("")
    setLocale("fr")
    setEmail("")
    triggerRef?.current?.focus()
  }

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && submittingRef.current) return
        if (!next) handleClose()
        else setOpen(true)
      }}
    >
      {/*
       * Radix Slot lets the child Button's `data-testid` win — don't
       * set one on the trigger wrapper. Parent page puts the locked
       * testid on its Button (`admin-new-{brand,agency}-button`).
       */}
      <ResponsiveDialogTrigger asChild>{trigger}</ResponsiveDialogTrigger>
      <ResponsiveDialogContent
        className="sm:max-w-md"
        data-testid={`${testId}-dialog`}
      >
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{labels.title}</ResponsiveDialogTitle>
          {/*
           * sr-only meaningful description — distinct from title so SR
           * users don't double-announce the dialog name on open.
           */}
          <ResponsiveDialogDescription className="sr-only">
            {labels.description}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        {/*
         * OKS-120 — Form className shim: `flex flex-1 min-h-0
         * overflow-y-auto p-4 sm:p-0` makes the Form the scrollable body
         * between sticky header/footer in mobile Sheet mode; inert in
         * desktop DialogContent (no flex parent → `flex-1` collapses).
         */}
        <Form
          action={adminInvitationsPath()}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={handleClose}
          resetOnSuccess
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-0"
        >
          {({ processing, errors }) => {
            submittingRef.current = processing
            return (
              <>
                {/* Path discriminator — backend's single controller. */}
                <input type="hidden" name="path" value={kind} />
                {/* Locale carries through Radix Select's hidden input via
                 * the `name` prop on `<Select>` below. */}

                {errors.base && (
                  <div
                    role="alert"
                    className="border-destructive/40 bg-destructive/10 text-foreground rounded-md border px-3 py-2 text-sm"
                    data-testid={`${testId}-error-banner`}
                  >
                    {Array.isArray(errors.base)
                      ? errors.base.join(" ")
                      : errors.base}
                  </div>
                )}
                {!online && (
                  <div
                    className="border-muted-foreground/20 bg-muted/40 text-muted-foreground rounded-md border px-3 py-2 text-sm"
                    data-testid={`${testId}-offline-banner`}
                  >
                    {t("admin.invites.dialog.offline_banner")}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <Label htmlFor="new-tenant-name">
                    {labels.name_label}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>
                  <Input
                    id="new-tenant-name"
                    name="name"
                    type="text"
                    autoComplete="off"
                    autoFocus
                    aria-required="true"
                    value={name}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setName(e.target.value)
                    }
                    placeholder={labels.name_placeholder}
                    aria-invalid={errors.name ? "true" : undefined}
                    aria-describedby={
                      errors.name ? "new-tenant-name-error" : undefined
                    }
                    disabled={!online || processing}
                  />
                  <InputError
                    messages={errors.name}
                    id="new-tenant-name-error"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="new-tenant-locale">
                    {labels.locale_label}
                  </Label>
                  <Select
                    items={{
                      fr: t("admin.invites.locale.fr"),
                      en: t("admin.invites.locale.en"),
                    }}
                    name="locale"
                    value={locale}
                    onValueChange={(value) => setLocale(value ?? "")}
                    disabled={!online || processing}
                  >
                    <SelectTrigger
                      id="new-tenant-locale"
                      aria-invalid={errors.locale ? "true" : undefined}
                      aria-describedby={
                        errors.locale ? "new-tenant-locale-error" : undefined
                      }
                      data-testid={`${testId}-locale`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">
                        {t("admin.invites.locale.fr")}
                      </SelectItem>
                      <SelectItem value="en">
                        {t("admin.invites.locale.en")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <InputError
                    messages={errors.locale}
                    id="new-tenant-locale-error"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="new-tenant-first-admin-email">
                    {labels.email_label}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>
                  <Input
                    id="new-tenant-first-admin-email"
                    name="admin_email"
                    type="email"
                    autoComplete="off"
                    aria-required="true"
                    value={email}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setEmail(e.target.value)
                    }
                    onBlur={handleEmailBlur}
                    placeholder={labels.email_placeholder}
                    aria-invalid={errors.admin_email ? "true" : undefined}
                    aria-describedby={
                      errors.admin_email ? "new-tenant-email-error" : undefined
                    }
                    disabled={!online || processing}
                  />
                  <InputError
                    messages={errors.admin_email}
                    id="new-tenant-email-error"
                  />
                </div>

                <ResponsiveDialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={processing}
                    aria-disabled={processing ? "true" : undefined}
                  >
                    {labels.cancel}
                  </Button>
                  <Button
                    type="submit"
                    disabled={processing || !online}
                    aria-disabled={processing || !online ? "true" : undefined}
                    data-testid={`${testId}-submit`}
                  >
                    {processing && (
                      <Loader2
                        className="size-4 animate-spin"
                        aria-hidden="true"
                      />
                    )}
                    {processing ? labels.creating : labels.submit}
                  </Button>
                </ResponsiveDialogFooter>
              </>
            )
          }}
        </Form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
